import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldAlert } from "lucide-react";

const EMPTY = {
  category: "Safety Judgement", qtype: "multiple_choice", question: "", options_text: "",
  correct_answer: "", explanation: "", difficulty: 1, safety_critical: false, points: 1, active: true,
};

export default function QuestionBank() {
  const { user } = useAuth();
  const isManagement = user?.role === "management_admin";
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | {id?} form
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/questions").then((r) => {
    setQuestions(r.data.questions);
    setCategories(r.data.categories);
    setLoading(false);
  }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (catFilter === "all" ? questions : questions.filter((q) => q.category === catFilter)),
    [questions, catFilter]
  );

  const openEdit = (q) => {
    setEditing(q
      ? { ...q, options_text: (q.options || []).join("\n") }
      : { ...EMPTY });
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      category: editing.category, qtype: editing.qtype, question: editing.question,
      options: editing.qtype === "numerical" ? [] : editing.options_text.split("\n").map((s) => s.trim()).filter(Boolean),
      correct_answer: editing.correct_answer, explanation: editing.explanation,
      difficulty: Number(editing.difficulty), safety_critical: !!editing.safety_critical,
      points: Number(editing.points), active: !!editing.active,
    };
    try {
      if (editing.id) await api.put(`/admin/questions/${editing.id}`, payload);
      else await api.post("/admin/questions", payload);
      toast.success("Question saved.");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const toggle = async (q) => {
    try {
      await api.post(`/admin/questions/${q.id}/toggle`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (q) => {
    if (!window.confirm("Delete this question permanently?")) return;
    try {
      await api.delete(`/admin/questions/${q.id}`);
      toast.success("Question deleted.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="questions-loading" />;

  const activeCount = questions.filter((q) => q.active).length;

  return (
    <div className="space-y-6" data-testid="question-bank-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Assessment Engine</p>
          <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="question-bank-heading">
            Question Bank
          </h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="question-count">
            {questions.length} questions — {activeCount} active, {questions.length - activeCount} inactive
          </p>
        </div>
        {isManagement && (
          <Button onClick={() => openEdit(null)} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="add-question-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Question
          </Button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="category-filters">
        {["all", ...categories].map((c) => (
          <button key={c} onClick={() => setCatFilter(c)}
            className={`px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest border transition-colors ${
              catFilter === c ? "bg-lomond text-black border-lomond font-bold" : "border-steel text-slate-500 hover:border-lomond hover:text-lomond"
            }`}
            data-testid={`cat-filter-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            {c === "all" ? "All" : c}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="questions-list">
        {filtered.map((q) => (
          <div key={q.id} className={`bg-slate-panel border rounded-sm p-4 sm:p-5 ${q.active ? "border-steel" : "border-steel opacity-60"}`} data-testid={`question-card-${q.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest text-slate-600 border-steel">{q.category}</Badge>
                  <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest text-slate-500 border-steel">{q.qtype.replace(/_/g, " ")}</Badge>
                  <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest text-slate-500 border-steel">D{q.difficulty}</Badge>
                  <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest text-slate-500 border-steel">{q.points} pt{q.points > 1 ? "s" : ""}</Badge>
                  {q.safety_critical && (
                    <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest text-red-400 border-red-500/40 bg-red-500/10">
                      <ShieldAlert className="h-3 w-3 mr-1" /> Safety critical
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-charcoal leading-relaxed">{q.question}</p>
                <p className="text-xs text-emerald-300 mt-1.5">Correct: {q.correct_answer}</p>
                {q.explanation && <p className="text-xs text-slate-500 mt-1">{q.explanation}</p>}
              </div>
              {isManagement && (
                <div className="flex items-center gap-2 shrink-0">
                  <Switch checked={q.active} onCheckedChange={() => toggle(q)} data-testid={`toggle-${q.id}`} />
                  <Button variant="outline" size="icon" onClick={() => openEdit(q)} className="border-steel text-slate-600 bg-transparent hover:border-lomond h-9 w-9" data-testid={`edit-${q.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => remove(q)} className="border-steel text-red-400 bg-transparent hover:border-red-500 h-9 w-9" data-testid={`delete-${q.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
        {!filtered.length && <p className="text-sm text-slate-500 text-center py-10" data-testid="no-questions">No questions in this category.</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-slate-panel border-steel text-foreground max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="question-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-charcoal">{editing?.id ? "Edit Question" : "Add Question"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-600 text-sm">Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger className="bg-obsidian border-steel text-charcoal" data-testid="qe-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-600 text-sm">Question type</Label>
                  <Select value={editing.qtype} onValueChange={(v) => setEditing({ ...editing, qtype: v })}>
                    <SelectTrigger className="bg-obsidian border-steel text-charcoal" data-testid="qe-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[["multiple_choice", "Multiple choice"], ["true_false", "True / False"], ["numerical", "Numerical"], ["scenario", "Scenario judgement"], ["multiple_select", "Multiple select"], ["image_hazard", "Image hazard recognition"]].map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-600 text-sm">Question</Label>
                <Textarea value={editing.question} onChange={(e) => setEditing({ ...editing, question: e.target.value })} className="bg-obsidian border-steel text-charcoal min-h-24" data-testid="qe-question" />
              </div>
              {editing.qtype !== "numerical" && (
                <div className="space-y-1.5">
                  <Label className="text-slate-600 text-sm">Answer options (one per line — order is randomised for candidates)</Label>
                  <Textarea value={editing.options_text} onChange={(e) => setEditing({ ...editing, options_text: e.target.value })} className="bg-obsidian border-steel text-charcoal min-h-28 font-mono text-sm" data-testid="qe-options" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-slate-600 text-sm">Correct answer (must match an option exactly, or the number for numerical)</Label>
                <Input value={editing.correct_answer} onChange={(e) => setEditing({ ...editing, correct_answer: e.target.value })} className="bg-obsidian border-steel text-charcoal" data-testid="qe-correct" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-600 text-sm">Explanation (shown to management / used in training)</Label>
                <Textarea value={editing.explanation} onChange={(e) => setEditing({ ...editing, explanation: e.target.value })} className="bg-obsidian border-steel text-charcoal" data-testid="qe-explanation" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-600 text-sm">Difficulty (1-3)</Label>
                  <Select value={String(editing.difficulty)} onValueChange={(v) => setEditing({ ...editing, difficulty: Number(v) })}>
                    <SelectTrigger className="bg-obsidian border-steel text-charcoal" data-testid="qe-difficulty"><SelectValue /></SelectTrigger>
                    <SelectContent>{["1", "2", "3"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-600 text-sm">Points</Label>
                  <Input type="number" min={1} value={editing.points} onChange={(e) => setEditing({ ...editing, points: e.target.value })} className="bg-obsidian border-steel text-charcoal" data-testid="qe-points" />
                </div>
                <div className="flex items-end gap-2 pb-1">
                  <Switch checked={editing.safety_critical} onCheckedChange={(v) => setEditing({ ...editing, safety_critical: v })} data-testid="qe-safety-critical" />
                  <Label className="text-slate-600 text-sm">Safety critical</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} className="border-steel text-slate-700 bg-transparent" data-testid="qe-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving || !editing?.question || !editing?.correct_answer} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="qe-save">
              {saving ? "Saving..." : "Save Question"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
